import { Vue, Component } from 'vue-property-decorator';
import APIService from '../../services/APIService';
import SubjectResponse from '../../models/responses/SubjectResponse';
import ApprenticeOverviewResponse from '../../models/responses/ApprenticeOverviewResponse';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner.vue';

/**
 * Displays a single apprentice, its semesters and subjects from one semester
 */
@Component({
  name: 'ApprenticeDetailView',
  components: {
    LoadingSpinner,
  },
})
export default class ApprenticeDetailView extends Vue {
  private apprenticeDetail: ApprenticeOverviewResponse = {
    apprentice: null,
    currentSemester: null,
    currentSubjects: [],
    semesters: [],
  };
  private loading = false;

  /**
   * Fetches apprentice detail data from the backend.
   */
  async mounted() {
    const semesterId = this.$route.query.semester as string | undefined;
    await this.getApprenticeDetail(semesterId);

    this.$store.commit('setBackNavigation', {
      route: '/dashboard',
      textKey: 'coachDashboard.title',
    });
  }

  /**
   * Gets apprentice detail response from api service.
   *
   * @param semesterId - id of semester
   */
  private async getApprenticeDetail(semesterId?: string) {
    this.loading = true;
    this.apprenticeDetail = await APIService.getApprenticeDetail(
      this.$route.params.id,
      semesterId,
    );
    this.loading = false;
  }

  /**
   * Sets a semester if selected in tabs.
   *
   * @param semesterId - id of semester
   */
  private async selectSemester(semesterId: string) {
    this.$router.replace({ query: { semester: semesterId } });
    this.apprenticeDetail.currentSubjects = [];
    await this.getApprenticeDetail(semesterId);
  }

  /**
   * Forwards to subject detail page.
   *
   * @param subject - selected subject
   */
  private goToSubjectDetail(subject: SubjectResponse) {
    this.$router.push('/subject-detail/' + subject.id);
  }

  /**
   * Triggers APIService to remove the coach of an apprentice.
   */
  private async removeApprentice() {
    if (!this.apprenticeDetail.apprentice) return;
    await APIService.removeApprentice(this.apprenticeDetail.apprentice.id);
    this.$router.push('/dashboard');
  }

  /**
   * Opens modal to confirm apprentice removal
   */
  private openConfirmationModal() {
    this.$store.commit('openModal', {
      title: this.$t('apprenticeDetail.removeApprentice'),
      text: this.$t('apprenticeDetail.removeApprenticeConfirmation'),
      confirmationCallback: this.removeApprentice,
    });
  }
}
